import { useEffect, useState, useMemo } from "react";
import { Plus, Trash2, ChevronDown, ChevronRight, Edit2, X, UserPlus, Factory, Lock, Unlock } from "lucide-react";
import { btn } from "@/components/common/btn";
import { getManufacturers, createManufacturer, updateManufacturer, deleteManufacturer, getMasterScopes, createMasterScope, verifyAdminPasscode } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { SettingsPanel, PanelHeader, PanelToolbar, EmptyState, LoadingState } from "@/components/settings/SettingsUI";
import { consumeFocusTarget, scrollToTestId, FOCUS_VENDOR } from "@/lib/deepLinkFocus";

// Passcode is validated server-side at POST /admin/verify-passcode (C2).
// The frontend collects and forwards; it does NOT know the value.

export default function Manufacturers({ autoAddMode, onManufacturerAdded }) {
  const [manufacturers, setManufacturers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  // iter274 — Cmd+K deep-link: expand the vendor the user picked.
  useEffect(() => {
    if (!manufacturers.length) return;
    const target = consumeFocusTarget(FOCUS_VENDOR);
    if (!target || !manufacturers.some((m) => m.id === target)) return;
    setSearch("");
    setExpandedId(target);
    scrollToTestId(`manufacturer-${target}`);
  }, [manufacturers]);  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(null);
  const [newName, setNewName] = useState("");
  const [search, setSearch] = useState("");
  const [scopes, setScopes] = useState([]);
  const [newScopeDialogOpen, setNewScopeDialogOpen] = useState(false);
  const [newScopeName, setNewScopeName] = useState("");
  const [newMfrScopes, setNewMfrScopes] = useState([]);
  const [newVendorNo, setNewVendorNo] = useState("");
  const [showAddForm, setShowAddForm] = useState(false);
  const [passwordPrompt, setPasswordPrompt] = useState(null);
  const [passwordInput, setPasswordInput] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (autoAddMode) {
      setShowAddForm(true);
      setNewName("");
      setNewMfrScopes([]);
    }
  }, [autoAddMode]);

  const load = async () => {
    try {
      const [mfgRes, scopeRes] = await Promise.all([getManufacturers(), getMasterScopes()]);
      setManufacturers(mfgRes.data);
      setScopes(scopeRes.data);
    } catch { toast.error("Failed to load vendors"); }
    finally { setLoading(false); }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return;
    try {
      const res = await createManufacturer({ name: newName.trim(), vendor_no: newVendorNo.trim(), scopes: newMfrScopes });
      setNewName(""); setNewVendorNo(""); setNewMfrScopes([]);
      setShowAddForm(false);
      toast.success("Vendor added");
      await load();
      if (onManufacturerAdded) onManufacturerAdded(res.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to add"); }
  };

  const handleDelete = async (id) => {
    try {
      await deleteManufacturer(id);
      toast.success("Vendor deleted");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to delete"); }
  };

  const startEdit = (mfr) => {
    setEditingId(mfr.id);
    setExpandedId(mfr.id);
    setForm({
      name: mfr.name || "", vendor_no: mfr.vendor_no || "", scopes: mfr.scopes || [],
      purchasing_contact_name: mfr.purchasing_contact_name || "",
      purchasing_contact_email: mfr.purchasing_contact_email || "",
      purchasing_contact_phone: mfr.purchasing_contact_phone || "",
      additional_contacts: mfr.additional_contacts || [],
      address: mfr.address || "", notes: mfr.notes || "",
    });
  };

  const cancelEdit = () => { setEditingId(null); setForm(null); };

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error("Vendor name required"); return; }
    try {
      await updateManufacturer(editingId, form);
      toast.success("Vendor updated");
      setEditingId(null); setForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const addAdditionalContact = () => {
    setForm(f => ({ ...f, additional_contacts: [...f.additional_contacts, { id: crypto.randomUUID(), name: "", email: "", phone: "", position: "", notes: "" }] }));
  };
  const updateAdditionalContact = (idx, field, value) => {
    setForm(f => { const c = [...f.additional_contacts]; c[idx] = { ...c[idx], [field]: value }; return { ...f, additional_contacts: c }; });
  };
  const removeAdditionalContact = (idx) => {
    setForm(f => ({ ...f, additional_contacts: f.additional_contacts.filter((_, i) => i !== idx) }));
  };

  const fmtPhone = (val) => {
    const d = val.replace(/\D/g, "").slice(0, 10);
    if (d.length <= 3) return d;
    if (d.length <= 6) return `${d.slice(0,3)}-${d.slice(3)}`;
    return `${d.slice(0,3)}-${d.slice(3,6)}-${d.slice(6)}`;
  };

  const handleAddNewScope = async () => {
    if (!newScopeName.trim()) return;
    try {
      await createMasterScope({ name: newScopeName.trim() });
      toast.success("Scope created");
      setNewScopeName(""); setNewScopeDialogOpen(false);
      const scopeRes = await getMasterScopes();
      setScopes(scopeRes.data);
    } catch { toast.error("Failed to create scope"); }
  };

  const checkPassword = (action, payload) => {
    setPasswordInput("");
    setPasswordPrompt({ action, payload });
  };

  const onPasswordSubmit = async () => {
    if (!passwordInput) return;
    try {
      // Server-side verify (C2). Even if a malicious user bypasses the
      // React flow with curl, the protected actions below still require
      // a valid verify_token — but the admin-mode boolean here is only
      // used to gate UI affordances, not to authorize mutations.
      await verifyAdminPasscode(passwordInput);
    } catch (e) {
      const code = e?.response?.status;
      const msg = code === 429
        ? (e.response?.data?.detail || "Too many attempts. Try again later.")
        : "Incorrect password";
      toast.error(msg);
      setPasswordPrompt(null); setPasswordInput("");
      return;
    }
    const { action, payload } = passwordPrompt;
    setPasswordPrompt(null); setPasswordInput("");
    if (action === "vendor") {
      setShowAddForm(true); setNewName(""); setNewVendorNo(""); setNewMfrScopes([]);
    } else if (action === "scope") {
      setNewScopeDialogOpen(true); setNewScopeName("");
    } else if (action === "delete") {
      handleDelete(payload);
    } else if (action === "unlock") {
      setIsAdmin(true);
      toast.success("Admin mode unlocked");
    }
  };

  const getScopeNames = (scopeIds) => (scopeIds || []).map(sid => scopes.find(s => s.id === sid)?.name).filter(Boolean);

  const filtered = manufacturers.filter(m => !search || m.name.toLowerCase().includes(search.toLowerCase()));

  const ScopeSelector = ({ selectedScopes, onChange, label = "Scopes" }) => (
    <div>
      <Label className="text-xs font-semibold">{label}</Label>
      <div className="flex flex-wrap gap-1.5 mt-1 p-2 border rounded-md bg-white min-h-[36px]">
        {scopes.map(scope => {
          const sel = selectedScopes.includes(scope.id);
          return (
            <button key={scope.id} type="button"
              className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${sel ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}
              onClick={() => onChange(sel ? selectedScopes.filter(s => s !== scope.id) : [...selectedScopes, scope.id])}
              data-testid={`scope-toggle-${scope.id}`}
            >{scope.name}</button>
          );
        })}
        <button type="button" className="text-[11px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-400 hover:text-slate-600 hover:border-slate-500 transition-colors"
          onClick={() => checkPassword("scope")} data-testid="btn-add-new-scope">
          <Plus className="w-3 h-3 inline -mt-0.5" /> New
        </button>
      </div>
    </div>
  );

  if (loading) return <SettingsPanel><LoadingState /></SettingsPanel>;

  return (
    <div data-testid="manufacturers-settings">
      <SettingsPanel>
        <PanelHeader icon={Factory} title="Vendors" count={filtered.length || null} hint="Suppliers mapped to equipment scopes" />
        <PanelToolbar
          searchValue={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search vendors…"
          searchTestId="search-manufacturers"
          actions={
            <button type="button" className={btn("write")} onClick={() => checkPassword("vendor")} data-testid="btn-show-add-manufacturer">
              <Plus className="w-3.5 h-3.5" /> Vendor
            </button>
          }
        />
        <div className="px-5 py-4 space-y-4">

        {showAddForm && (
          <div className="border rounded-lg p-4 mb-4 bg-emerald-50/30 space-y-3" data-testid="add-manufacturer-form">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-semibold">New Vendor</Label>
              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setShowAddForm(false); if (onManufacturerAdded) onManufacturerAdded(null); }}><X className="w-4 h-4" /></Button>
            </div>
            <div className="grid grid-cols-[100px_1fr] gap-2">
              <div>
                <Label className="text-xs">Vendor No</Label>
                <Input value={newVendorNo} onChange={e => setNewVendorNo(e.target.value)} placeholder="e.g., 126" data-testid="input-new-vendor-no" />
              </div>
              <div>
                <Label className="text-xs">Vendor Name *</Label>
                <Input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Vendor name" data-testid="input-new-manufacturer"
                  onKeyDown={e => { if (e.key === "Enter") handleAdd(); }} autoFocus />
              </div>
            </div>
            <ScopeSelector selectedScopes={newMfrScopes} onChange={setNewMfrScopes} label="Assign to Scopes" />
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={handleAdd} data-testid="btn-add-manufacturer">Save Vendor</Button>
              <Button size="sm" variant="outline" onClick={() => { setShowAddForm(false); if (onManufacturerAdded) onManufacturerAdded(null); }}>Cancel</Button>
            </div>
          </div>
        )}

        <div className="space-y-1">
          {filtered.length === 0 && <EmptyState icon={Factory} title="No vendors found" hint="Try a different search, or add a vendor." />}
          {filtered.map(mfr => {
            const isExpanded = expandedId === mfr.id;
            const isEditing = editingId === mfr.id;
            const mfrScopeNames = getScopeNames(mfr.scopes);

            return (
              <div key={mfr.id} className="border rounded-lg bg-white" data-testid={`manufacturer-${mfr.id}`}>
                <div className="flex items-center gap-2 px-3 py-2.5 cursor-pointer hover:bg-slate-50" onClick={() => setExpandedId(isExpanded ? null : mfr.id)}>
                  <button className="shrink-0 text-slate-400">
                    {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-mono text-slate-400 mr-2">{mfr.vendor_no || "—"}</span>
                    <span className="text-sm font-medium text-slate-800">{mfr.name}</span>
                    {mfrScopeNames.length > 0 && <span className="ml-2 text-[10px] text-slate-400">{mfrScopeNames.join(", ")}</span>}
                    {mfr.purchasing_contact_email && <span className="ml-3 text-xs text-slate-400">PO: {mfr.purchasing_contact_name || mfr.purchasing_contact_email}</span>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEdit(mfr)} data-testid={`edit-manufacturer-${mfr.id}`}>
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => checkPassword("delete", mfr.id)} data-testid={`delete-manufacturer-${mfr.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {isExpanded && (
                  <div className="border-t px-4 py-3 bg-slate-50/50 space-y-4">
                    {isEditing && form ? (
                      <>
                        {/* Vendor No + Name — locked unless admin */}
                        <div className="grid grid-cols-[100px_1fr_auto] gap-2 items-end">
                          <div>
                            <Label className="text-xs font-semibold">Vendor No {!isAdmin && <Lock className="w-3 h-3 inline text-slate-300 ml-0.5" />}</Label>
                            <Input className="h-8 text-sm mt-1" value={form.vendor_no} onChange={e => setForm(f => ({...f, vendor_no: e.target.value}))} disabled={!isAdmin} data-testid="edit-mfr-vendor-no" />
                          </div>
                          <div>
                            <Label className="text-xs font-semibold">Vendor Name {!isAdmin && <Lock className="w-3 h-3 inline text-slate-300 ml-0.5" />}</Label>
                            <Input className="h-8 text-sm mt-1" value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} disabled={!isAdmin} data-testid="edit-mfr-name" />
                          </div>
                          {!isAdmin && (
                            <Button variant="outline" size="sm" className="h-8 text-[10px] gap-1" onClick={() => checkPassword("unlock")} data-testid="btn-unlock-admin">
                              <Unlock className="w-3 h-3" /> Unlock
                            </Button>
                          )}
                        </div>

                        {/* Scopes — always editable */}
                        <ScopeSelector selectedScopes={form.scopes} onChange={(s) => setForm(f => ({...f, scopes: s}))} />

                        {/* Purchasing Contact — always editable */}
                        <div className="p-3 border rounded-md bg-blue-50/50">
                          <Label className="text-xs font-semibold text-blue-700 block mb-2">Purchasing Contact (POs sent here)</Label>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-[10px] text-slate-500">Name</Label>
                              <Input className="h-7 text-xs" value={form.purchasing_contact_name} onChange={e => setForm(f => ({...f, purchasing_contact_name: e.target.value}))} placeholder="Contact name" data-testid="edit-mfr-po-name" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-slate-500">Email</Label>
                              <Input className="h-7 text-xs" type="email" value={form.purchasing_contact_email} onChange={e => setForm(f => ({...f, purchasing_contact_email: e.target.value}))} placeholder="orders@vendor.com" data-testid="edit-mfr-po-email" />
                            </div>
                            <div>
                              <Label className="text-[10px] text-slate-500">Phone</Label>
                              <Input className="h-7 text-xs" value={fmtPhone(form.purchasing_contact_phone)} onChange={e => setForm(f => ({...f, purchasing_contact_phone: fmtPhone(e.target.value)}))} placeholder="303-333-3333" maxLength={12} data-testid="edit-mfr-po-phone" />
                            </div>
                          </div>
                        </div>

                        {/* Address — always editable */}
                        <div>
                          <Label className="text-xs font-semibold">Address</Label>
                          <Input className="h-8 text-sm mt-1" value={form.address} onChange={e => setForm(f => ({...f, address: e.target.value}))} placeholder="123 Main St, City, ST 00000" data-testid="edit-mfr-address" />
                        </div>

                        {/* Additional Contacts — always editable */}
                        <div>
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-xs font-semibold">Additional Contacts</Label>
                            <Button variant="outline" size="sm" className="h-6 text-[10px]" onClick={addAdditionalContact} data-testid="btn-add-additional-contact">
                              <UserPlus className="w-3 h-3 mr-1" /> Add Contact
                            </Button>
                          </div>
                          {form.additional_contacts.length === 0 && <div className="text-[11px] text-slate-400 italic py-2 text-center">No additional contacts</div>}
                          {form.additional_contacts.map((c, idx) => (
                            <div key={c.id || idx} className="border rounded p-2 mb-2 bg-white" data-testid={`additional-contact-${idx}`}>
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-[10px] text-slate-400 uppercase font-semibold">Contact {idx + 1}</span>
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-red-400 hover:text-red-600" onClick={() => removeAdditionalContact(idx)}><X className="w-3 h-3" /></Button>
                              </div>
                              <div className="grid grid-cols-2 gap-1.5">
                                <Input className="h-7 text-xs" placeholder="Name" value={c.name} onChange={e => updateAdditionalContact(idx, "name", e.target.value)} />
                                <Input className="h-7 text-xs" placeholder="Position" value={c.position} onChange={e => updateAdditionalContact(idx, "position", e.target.value)} />
                                <Input className="h-7 text-xs" placeholder="303-333-3333" value={fmtPhone(c.phone)} onChange={e => updateAdditionalContact(idx, "phone", fmtPhone(e.target.value))} maxLength={12} />
                                <Input className="h-7 text-xs" placeholder="Email" value={c.email} onChange={e => updateAdditionalContact(idx, "email", e.target.value)} />
                              </div>
                              <Input className="h-7 text-xs mt-1.5" placeholder="Notes" value={c.notes} onChange={e => updateAdditionalContact(idx, "notes", e.target.value)} />
                            </div>
                          ))}
                        </div>

                        {/* Notes — always editable */}
                        <div>
                          <Label className="text-xs font-semibold">Notes</Label>
                          <Textarea className="text-sm mt-1 min-h-[60px]" value={form.notes} onChange={e => setForm(f => ({...f, notes: e.target.value}))} placeholder="Internal notes about this vendor..." data-testid="edit-mfr-notes" />
                        </div>

                        <div className="flex gap-2 pt-1">
                          <Button size="sm" onClick={handleSave} data-testid="btn-save-manufacturer">Save</Button>
                          <Button size="sm" variant="outline" onClick={cancelEdit}>Cancel</Button>
                        </div>
                      </>
                    ) : (
                      <div className="space-y-2 text-sm">
                        {mfrScopeNames.length > 0 && (
                          <div>
                            <span className="text-xs text-slate-400 uppercase font-semibold">Scopes:</span>
                            <span className="ml-1.5">{mfrScopeNames.map((name) => (
                              <span key={`msc-${name}`} className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 border border-slate-200 mr-1">{name}</span>
                            ))}</span>
                          </div>
                        )}
                        {mfr.address && <div><span className="text-xs text-slate-400 uppercase font-semibold">Address:</span> <span className="text-slate-700">{mfr.address}</span></div>}
                        <div className="p-2 border rounded bg-blue-50/30">
                          <span className="text-[10px] text-blue-600 uppercase font-semibold block mb-1">Purchasing Contact</span>
                          {mfr.purchasing_contact_name || mfr.purchasing_contact_email ? (
                            <div className="text-xs space-y-0.5">
                              {mfr.purchasing_contact_name && <div className="font-medium">{mfr.purchasing_contact_name}</div>}
                              {mfr.purchasing_contact_email && <a href={`mailto:${mfr.purchasing_contact_email}`} className="text-blue-500 hover:underline block">{mfr.purchasing_contact_email}</a>}
                              {mfr.purchasing_contact_phone && <a href={`tel:${mfr.purchasing_contact_phone}`} className="text-blue-500 hover:underline block">{mfr.purchasing_contact_phone}</a>}
                            </div>
                          ) : <div className="text-xs text-slate-400 italic">Not set — click Edit to add</div>}
                        </div>
                        {(mfr.additional_contacts || []).length > 0 && (
                          <div>
                            <span className="text-[10px] text-slate-400 uppercase font-semibold block mb-1">Additional Contacts ({mfr.additional_contacts.length})</span>
                            {mfr.additional_contacts.map((c, idx) => (
                              <div key={c.id || idx} className="text-xs border-l-2 border-slate-200 pl-2 mb-1.5">
                                <span className="font-medium">{c.name || "Unnamed"}</span>
                                {c.position && <span className="text-slate-400 ml-1">({c.position})</span>}
                                {c.phone && <span className="ml-2 text-slate-500">{c.phone}</span>}
                                {c.email && <span className="ml-2 text-blue-500">{c.email}</span>}
                              </div>
                            ))}
                          </div>
                        )}
                        {mfr.notes && <div><span className="text-xs text-slate-400 uppercase font-semibold">Notes:</span> <span className="text-xs text-slate-600">{mfr.notes}</span></div>}
                        {!mfr.purchasing_contact_email && !mfr.address && !(mfr.additional_contacts || []).length && !mfr.notes && mfrScopeNames.length === 0 && (
                          <div className="text-xs text-slate-400 italic text-center py-2">No details added yet — click Edit to configure</div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        </div>
      </SettingsPanel>

      {newScopeDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setNewScopeDialogOpen(false)}>
          <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-sm" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Add New Scope</h3>
            <Input value={newScopeName} onChange={e => setNewScopeName(e.target.value)} placeholder="e.g. HVAC, Bleachers, Fire Safety" autoFocus
              onKeyDown={e => { if (e.key === "Enter") handleAddNewScope(); }} data-testid="input-new-scope-inline" />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setNewScopeDialogOpen(false)}>Cancel</Button>
              <Button size="sm" onClick={handleAddNewScope} data-testid="btn-save-new-scope-inline">Save</Button>
            </div>
          </div>
        </div>
      )}

      {passwordPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={() => setPasswordPrompt(null)}>
          <div className="bg-white rounded-lg shadow-xl p-5 w-full max-w-xs" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-semibold mb-3" style={{ fontFamily: 'Manrope, sans-serif' }}>Enter Password</h3>
            <Input type="password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} placeholder="Password" autoFocus
              onKeyDown={e => { if (e.key === "Enter") onPasswordSubmit(); }} data-testid="input-password-prompt" />
            <div className="flex justify-end gap-2 mt-3">
              <Button variant="outline" size="sm" onClick={() => setPasswordPrompt(null)}>Cancel</Button>
              <Button size="sm" onClick={onPasswordSubmit} data-testid="btn-password-submit">Submit</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
